// Pack Editor — load a saved IndustryPack, show its company/products/
// personas/stories in editable cards, support inline AI refinement, and
// PUT updates back to /api/packs/{id}. This is the v2 replacement for
// the prod app's PackEditor.jsx — slimmer, focused on the most common
// edit needs (company metadata + AI refine), with full JSON access for
// power users.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Ajv2020 from "ajv/dist/2020";
import { apiCall, apiSoft } from "@/lib/api/http";
import { aiApi, type PackJson } from "@/lib/api/ai";
import { api } from "@/lib/api";
import { useUiStore } from "@/lib/store/uiStore";
import { useConfigStore } from "@/lib/store/configStore";
import {
  schema as packSchema,
  boardCanonicalOptions,
  metricCanonicalOptions,
  pulseClassificationOptions,
  pulseSubscriptionChannelOptions,
} from "@/lib/schema/packSchema";

// PR 3 Week 2 — story IDs that the runtime expects. The schema's
// stories.descriptions.required list is the SSoT; we mirror it
// statically here so the StoriesTab can render the 16 textareas
// in a stable order without re-deriving from the schema on every
// render.
const STORY_IDS: readonly string[] = [
  "pipeline_health_crisis",
  "rep_ramp_trajectory",
  "product_mix_shift",
  "lead_source_roi",
  "regional_performance_gap",
  "forecast_accuracy",
  "account_turnaround",
  "satisfaction_transformation",
  "agent_performance_spotlight",
  "regional_service_quality",
  "response_time_satisfaction",
  "omni_channel_optimization",
  "knowledge_base_roi",
  "product_support_excellence",
  "sla_compliance_journey",
  "premium_support_roi",
] as const;

type Tab =
  | "company"
  | "products"
  | "tableau_next"
  | "tableau_outputs"
  | "date_shift"
  | "tab_extensions"
  | "stories"
  | "validate"
  | "json";

interface PackVersion {
  id: string;
  version_number: number;
  created_at?: string | null;
  change_summary?: string | null;
  pack_json?: PackJson | null;
}

const STATUS_BADGES: Record<string, { label: string; bg: string; fg: string }> = {
  built_in: { label: "Built-in", bg: "var(--eb-50)", fg: "white" },
  featured: { label: "Featured", bg: "#fcc003", fg: "var(--eb-15)" },
  approved: { label: "Community", bg: "rgba(4,225,203,.18)", fg: "var(--teal)" },
  draft: { label: "Draft", bg: "#D5E5F2", fg: "#4A6785" },
  submitted: { label: "Pending review", bg: "rgba(168,110,233,.18)", fg: "var(--violet)" },
  rejected: { label: "Needs changes", bg: "rgba(255,83,138,.15)", fg: "var(--pink)" },
};

interface ProductRow {
  name?: string;
  code?: string;
  family?: string;
  description?: string;
  is_active?: boolean;
  sales?: {
    deal_size_range?: [number, number];
    win_rate_modifier?: number;
    cycle_days_range?: [number, number];
    selection_weight?: number;
  };
  support?: {
    cases_per_100_installs_quarter?: number;
    resolution_complexity_modifier?: number;
    csat_modifier?: number;
    case_type_weights?: Record<string, number>;
  };
  [k: string]: unknown;
}

interface PackRecord {
  id: string;
  name: string;
  description?: string | null;
  source?: string | null;
  status?: string | null;
  is_public?: boolean;
  pack_json?: PackJson | string | null;
  // Some endpoints return the full unwrapped pack body
  company?: PackJson["company"];
  products?: unknown[];
  personas?: unknown[];
  stories?: unknown;
}

function readPackJson(rec: PackRecord | undefined): PackJson {
  if (!rec) return {};
  const raw = rec.pack_json;
  if (raw && typeof raw === "string") {
    try {
      return JSON.parse(raw) as PackJson;
    } catch {
      return {};
    }
  }
  if (raw && typeof raw === "object") return raw;
  // GET /api/packs/{id} can return the unwrapped pack body directly.
  const { id: _id, name: _n, description: _d, source: _s, status: _st, ...rest } = rec;
  return rest as PackJson;
}

export function PackEditor(): JSX.Element {
  const { id: profileId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const showToast = useUiStore((s) => s.showToast);
  const setConfig = useConfigStore((s) => s.setConfig);
  const pickMode = useConfigStore((s) => s.pickMode);

  const { data: pack, isLoading, error } = useQuery({
    queryKey: ["pack", profileId],
    queryFn: () => apiCall<PackRecord>(`/api/packs/${encodeURIComponent(profileId)}`),
    enabled: !!profileId,
  });

  const { data: versions = [] } = useQuery<PackVersion[]>({
    queryKey: ["pack-versions", profileId],
    queryFn: () =>
      apiSoft<PackVersion[]>(
        `/api/packs/${encodeURIComponent(profileId)}/versions`,
      ).then((v) => v ?? []),
    enabled: !!profileId,
  });

  const [draft, setDraft] = useState<PackJson>({});
  const [packMeta, setPackMeta] = useState<{ name: string; description: string }>({
    name: "",
    description: "",
  });
  const [tab, setTab] = useState<Tab>("company");
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [refineLog, setRefineLog] = useState<{ at: string; text: string }[]>([]);
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  // F-H1 (2026-04-20): abort controller so leaving the page or
  // saving while a refine is in flight cancels the underlying job
  // poll instead of letting it churn /api/jobs/* in the background.
  const refineAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!pack) return;
    const body = readPackJson(pack);
    setDraft(body);
    setPackMeta({
      name: pack.name ?? body.company?.name ?? "Untitled pack",
      description: pack.description ?? body.company?.description ?? "",
    });
    setJsonText(JSON.stringify(body, null, 2));
    setDirty(false);
  }, [pack]);

  useEffect(() => {
    if (tab === "json") setJsonText(JSON.stringify(draft, null, 2));
  }, [tab, draft]);

  const saveMut = useMutation({
    mutationFn: async () => {
      return apiCall<{ id: string; status: string; version?: number }>(
        `/api/packs/${encodeURIComponent(profileId)}`,
        {
          method: "PUT",
          body: {
            name: packMeta.name,
            description: packMeta.description,
            pack_json: draft,
          },
        },
      );
    },
    onSuccess: (res) => {
      showToast(
        `Saved · v${res.version ?? "?"} — "${packMeta.name}"`,
      );
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["pack", profileId] });
      qc.invalidateQueries({ queryKey: ["packs"] });
      qc.invalidateQueries({ queryKey: ["profiles"] });
    },
    onError: (e) => {
      const msg = (e as Error).message ?? "Save failed";
      showToast(`Save failed — ${msg}`);
    },
  });

  const counts = useMemo(() => {
    const products = Array.isArray(draft.products) ? draft.products.length : 0;
    const personas = Array.isArray(draft.personas) ? draft.personas.length : 0;
    let stories = 0;
    if (draft.stories && typeof draft.stories === "object") {
      for (const v of Object.values(draft.stories as Record<string, unknown>)) {
        if (Array.isArray(v)) stories += v.length;
      }
    }
    return { products, personas, stories };
  }, [draft]);

  const updateCompany = (key: string, value: string): void => {
    setDirty(true);
    setDraft((d) => {
      const company = (d.company ?? {}) as Record<string, unknown>;
      return { ...d, company: { ...company, [key]: value } };
    });
  };

  const updateProducts = (next: ProductRow[]): void => {
    setDirty(true);
    setDraft((d) => ({ ...d, products: next }));
  };

  const submitMut = useMutation({
    mutationFn: () =>
      apiCall<{ status: string }>(
        `/api/packs/${encodeURIComponent(profileId)}/submit`,
        { method: "POST" },
      ),
    onSuccess: () => {
      showToast("Pack submitted for review");
      qc.invalidateQueries({ queryKey: ["pack", profileId] });
      qc.invalidateQueries({ queryKey: ["packs"] });
    },
    onError: (e) => showToast(`Submit failed: ${(e as Error).message}`),
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      apiCall<{ status: string }>(
        `/api/packs/${encodeURIComponent(profileId)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      showToast("Draft pack deleted");
      qc.invalidateQueries({ queryKey: ["packs"] });
      qc.invalidateQueries({ queryKey: ["profiles"] });
      navigate("/profiles");
    },
    onError: (e) => showToast(`Delete failed: ${(e as Error).message}`),
  });

  const exportJson = (): void => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const company = (draft.company ?? {}) as Record<string, string>;
    a.download = `${company.short_name || company.name?.replace(/\s+/g, "_").toLowerCase() || profileId}_industry_pack.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const testRunWithPack = (): void => {
    pickMode("custom");
    setConfig({ profileId });
    navigate("/configure");
  };

  const restoreVersion = async (versionNumber: number): Promise<void> => {
    try {
      const ver = await apiCall<PackVersion>(
        `/api/packs/${encodeURIComponent(profileId)}/versions/${versionNumber}`,
      );
      if (ver?.pack_json) {
        setDraft(ver.pack_json);
        setDirty(true);
        showToast(`Restored v${versionNumber} — review and save to keep`);
      }
    } catch (e) {
      showToast(`Could not restore: ${(e as Error).message}`);
    }
  };

  const runRefine = async (): Promise<void> => {
    const text = refinePrompt.trim();
    if (!text) return;
    setRefining(true);
    refineAbortRef.current?.abort();
    refineAbortRef.current = new AbortController();
    try {
      const res = await aiApi.refinePack(draft, text, [], {
        signal: refineAbortRef.current.signal,
      });
      setDraft(res.pack);
      setRefineLog((log) => [{ at: "just now", text: res.summary || text }, ...log]);
      setRefinePrompt("");
      setDirty(true);
      showToast(`Pack refined — review and save`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Refine failed";
      if (msg !== "Aborted") {
        showToast(`Refine failed: ${msg}`);
      }
    } finally {
      setRefining(false);
    }
  };

  useEffect(() => {
    return () => {
      refineAbortRef.current?.abort();
    };
  }, []);

  if (!profileId) {
    return <EditorMessage title="No profile selected" message="Open this page from /profiles." />;
  }
  if (isLoading) {
    return <EditorMessage title="Loading pack…" message="" />;
  }
  if (error || !pack) {
    return <EditorMessage title="Couldn't load pack" message={(error as Error | null)?.message ?? "Pack not found"} />;
  }

  const isBuiltIn = pack.source === "built_in" || pack.status === "built_in";
  const isReadOnly =
    isBuiltIn ||
    (pack.status && !["draft", "rejected"].includes(pack.status));
  const DEFAULT_BADGE = { label: "Draft", bg: "#D5E5F2", fg: "#4A6785" };
  const statusBadge =
    (pack.status ? STATUS_BADGES[pack.status] : null) ??
    STATUS_BADGES.draft ??
    DEFAULT_BADGE;

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 24px 120px" }}>
      <button
        onClick={() => navigate("/profiles")}
        className="btn ghost sm"
        style={{ marginBottom: 12 }}
      >
        ← Back to profiles
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            Pack editor
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 999,
                background: statusBadge.bg,
                color: statusBadge.fg,
                textTransform: "none",
                letterSpacing: 0,
              }}
            >
              {statusBadge.label}
            </span>
            {dirty && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(252,192,3,.18)",
                  color: "#704800",
                }}
              >
                Unsaved changes
              </span>
            )}
          </div>
          <input
            value={packMeta.name}
            onChange={(e) => {
              setDirty(true);
              setPackMeta((m) => ({ ...m, name: e.target.value }));
            }}
            disabled={!!isReadOnly}
            style={{
              fontSize: 28,
              fontWeight: 700,
              border: 0,
              background: "transparent",
              padding: 0,
              outline: "none",
              width: "100%",
              color: "var(--ink)",
            }}
          />
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
            {counts.products} products · {counts.personas} personas · {counts.stories} stories
            {pack.source && <> · source: {pack.source}</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            className="btn ghost"
            onClick={exportJson}
            title="Download the raw pack_json"
          >
            ↓ Export JSON
          </button>
          <button
            className="btn ghost"
            onClick={testRunWithPack}
            title="Open Configure pre-loaded with this pack"
          >
            Test run →
          </button>
          <button
            className="btn primary lg"
            disabled={!dirty || saveMut.isPending || !!isReadOnly}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {isReadOnly && (
        <div
          style={{
            padding: 12,
            background: "#FEF3D6",
            border: "1px solid #F8DA86",
            borderRadius: 8,
            fontSize: 12,
            color: "#704800",
            marginBottom: 16,
          }}
        >
          Pack status is <strong>{pack.status}</strong> — read-only. Fork the pack to edit it.
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: "inline-flex",
          background: "var(--bg-2)",
          border: "1px solid var(--bd)",
          borderRadius: 10,
          padding: 3,
          gap: 2,
          marginBottom: 16,
        }}
      >
        {([
          ["company", "Company"],
          ["products", `Products · ${counts.products}`],
          ["tableau_next", "Tableau Next"],
          ["tableau_outputs", "Cloud Pulse"],
          ["date_shift", "Date Shift"],
          ["tab_extensions", "Tab Extensions"],
          ["stories", "Stories"],
          ["validate", "Validate"],
          ["json", "Raw JSON"],
        ] as const).map(([id, label]) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                background: active ? "white" : "transparent",
                color: active ? "var(--eb-15)" : "var(--ink-2)",
                border: 0,
                borderRadius: 7,
                cursor: "pointer",
                boxShadow: active ? "0 1px 2px rgba(0,0,0,.06)" : "none",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: versions.length > 0 ? "minmax(0, 1fr) 280px" : "minmax(0, 1fr)",
          gap: 16,
          alignItems: "flex-start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          {tab === "company" && (
            <CompanyTab
              draft={draft}
              packMeta={packMeta}
              setPackMeta={setPackMeta}
              markDirty={() => setDirty(true)}
              updateCompany={updateCompany}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "products" && (
            <ProductsTab
              products={Array.isArray(draft.products) ? (draft.products as ProductRow[]) : []}
              onChange={updateProducts}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "tableau_next" && (
            <TableauNextTab
              draft={draft}
              onChange={(next) => {
                setDirty(true);
                setDraft((d) => ({ ...d, tableau_next: next }));
              }}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "tableau_outputs" && (
            <CloudPulseTab
              draft={draft}
              onChange={(next) => {
                setDirty(true);
                setDraft((d) => ({ ...d, tableau_outputs: next }));
              }}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "date_shift" && (
            <DateShiftTab
              draft={draft}
              onChange={(next) => {
                setDirty(true);
                setDraft((d) => ({ ...d, date_shift: next }));
              }}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "tab_extensions" && (
            <TabExtensionsTab
              draft={draft}
              onChange={(next) => {
                setDirty(true);
                setDraft((d) => ({ ...d, tab_extensions: next }));
              }}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "stories" && (
            <StoriesTab
              draft={draft}
              onChange={(next) => {
                setDirty(true);
                setDraft((d) => ({ ...d, stories: next }));
              }}
              readOnly={!!isReadOnly}
            />
          )}
          {tab === "validate" && (
            <ValidateTab draft={draft} />
          )}
          {tab === "json" && (
            <JsonTab
              jsonText={jsonText}
              setJsonText={(s) => {
                setJsonText(s);
                setJsonError(null);
              }}
              jsonError={jsonError}
              onApply={() => {
                try {
                  const parsed = JSON.parse(jsonText);
                  setDraft(parsed);
                  setDirty(true);
                  showToast("JSON applied — save to persist");
                } catch (e) {
                  setJsonError((e as Error).message);
                }
              }}
              readOnly={!!isReadOnly}
            />
          )}
        </div>
        {versions.length > 0 && (
          <VersionHistoryPanel
            versions={versions}
            onRestore={(n) => void restoreVersion(n)}
          />
        )}
      </div>

      {/* AI refine bar */}
      {!isReadOnly && (
        <div
          className="card"
          style={{
            marginTop: 18,
            padding: 14,
            borderColor: "var(--eb-50)",
            background: "#F4F8FC",
          }}
        >
          <div className="label" style={{ color: "var(--eb-50)", marginBottom: 8 }}>
            Refine with AI
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={refinePrompt}
              onChange={(e) => setRefinePrompt(e.target.value)}
              disabled={refining}
              placeholder="e.g. Add a CFO persona · double the volumes · change industry to FinServ"
              style={{
                flex: 1,
                padding: "10px 12px",
                fontSize: 12,
                border: "1px solid var(--bd)",
                borderRadius: 8,
                outline: "none",
                background: "white",
              }}
            />
            <button
              onClick={() => { void runRefine(); }}
              disabled={!refinePrompt.trim() || refining}
              className="btn primary"
            >
              {refining ? "Refining…" : "Refine →"}
            </button>
          </div>
          {refineLog.length > 0 && (
            <div style={{ marginTop: 10, display: "grid", gap: 4 }}>
              {refineLog.map((r, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 11,
                    color: "var(--ink-2)",
                    padding: "4px 8px",
                    background: "#EAF8F4",
                    borderLeft: "3px solid var(--teal)",
                    borderRadius: 4,
                  }}
                >
                  <span style={{ color: "var(--teal)" }}>✓</span> {r.text}{" "}
                  <span style={{ color: "var(--ink-3)" }}>· {r.at}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom action bar: submit / delete (drafts only) */}
      {!isReadOnly && (pack.status === "draft" || pack.status === "rejected") && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "16px 0",
            marginTop: 18,
            borderTop: "1px solid var(--bd-2)",
          }}
        >
          <button
            className="btn primary"
            style={{ background: "var(--violet)" }}
            onClick={() => {
              if (
                window.confirm(
                  "Submit this pack for curator review? You won't be able to edit it while it's pending.",
                )
              ) {
                submitMut.mutate();
              }
            }}
            disabled={submitMut.isPending}
          >
            {submitMut.isPending ? "Submitting…" : "Submit for review"}
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  "Delete this draft pack? This cannot be undone.",
                )
              ) {
                deleteMut.mutate();
              }
            }}
            disabled={deleteMut.isPending}
            style={{
              marginLeft: "auto",
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
              border: "1px solid var(--pink)",
              background: "transparent",
              color: "var(--pink)",
              borderRadius: 8,
              cursor: deleteMut.isPending ? "not-allowed" : "pointer",
            }}
          >
            {deleteMut.isPending ? "Deleting…" : "Delete draft"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────

function CompanyTab({
  draft,
  packMeta,
  setPackMeta,
  markDirty,
  updateCompany,
  readOnly,
}: {
  draft: PackJson;
  packMeta: { name: string; description: string };
  setPackMeta: (m: { name: string; description: string }) => void;
  markDirty: () => void;
  updateCompany: (k: string, v: string) => void;
  readOnly: boolean;
}): JSX.Element {
  const company = (draft.company ?? {}) as Record<string, string>;
  return (
    <div className="card" style={{ padding: 18 }}>
      <Field
        label="Description"
        value={packMeta.description}
        onChange={(v) => {
          markDirty();
          setPackMeta({ ...packMeta, description: v });
        }}
        readOnly={readOnly}
        multiline
      />
      <Field
        label="Industry"
        value={company.industry ?? ""}
        onChange={(v) => updateCompany("industry", v)}
        readOnly={readOnly}
      />
      <Field
        label="Tagline"
        value={company.tagline ?? ""}
        onChange={(v) => updateCompany("tagline", v)}
        readOnly={readOnly}
      />
      <Field
        label="Domain"
        value={company.domain ?? ""}
        onChange={(v) => updateCompany("domain", v)}
        readOnly={readOnly}
      />
    </div>
  );
}

function ProductsTab({
  products,
  onChange,
  readOnly,
}: {
  products: ProductRow[];
  onChange: (next: ProductRow[]) => void;
  readOnly: boolean;
}): JSX.Element {
  const updateProduct = (idx: number, patch: Partial<ProductRow>): void => {
    const next = products.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange(next);
  };
  const addProduct = (): void => {
    onChange([
      ...products,
      {
        name: "",
        code: "",
        family: "",
        description: "",
        is_active: true,
        sales: {
          deal_size_range: [10000, 100000],
          win_rate_modifier: 1.0,
          cycle_days_range: [30, 90],
          selection_weight: 0.1,
        },
        support: {
          cases_per_100_installs_quarter: 5,
          resolution_complexity_modifier: 1.0,
          csat_modifier: 0,
          case_type_weights: {},
        },
      },
    ]);
  };
  const removeProduct = (idx: number): void => {
    onChange(products.filter((_, i) => i !== idx));
  };

  if (products.length === 0 && readOnly) {
    return (
      <div className="card" style={{ padding: 18, color: "var(--ink-3)", fontSize: 13 }}>
        No products on this pack yet.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div className="label">Products · {products.length}</div>
        {!readOnly && (
          <button
            onClick={addProduct}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--eb-50)",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            + Add product
          </button>
        )}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {products.map((raw, i) => {
          const p: ProductRow = raw && typeof raw === "object" ? raw : {};
          return (
          <div key={i} className="card" style={{ padding: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 100px 1fr auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <ProductInput
                value={p.name ?? ""}
                placeholder="Product name"
                onChange={(v) => updateProduct(i, { name: v })}
                readOnly={readOnly}
              />
              <ProductInput
                value={p.code ?? ""}
                placeholder="Code"
                onChange={(v) => updateProduct(i, { code: v })}
                readOnly={readOnly}
              />
              <ProductInput
                value={p.family ?? ""}
                placeholder="Family"
                onChange={(v) => updateProduct(i, { family: v })}
                readOnly={readOnly}
              />
              {!readOnly && (
                <button
                  onClick={() => {
                    if (window.confirm(`Remove "${p.name || "this product"}"?`)) {
                      removeProduct(i);
                    }
                  }}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--pink)",
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "0 6px",
                  }}
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </div>
            <ProductInput
              value={p.description ?? ""}
              placeholder="Description"
              onChange={(v) => updateProduct(i, { description: v })}
              readOnly={readOnly}
              style={{ marginTop: 8, fontSize: 11 }}
            />
            <details style={{ marginTop: 8 }}>
              <summary
                style={{
                  fontSize: 11,
                  color: "var(--ink-3)",
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 500,
                }}
              >
                Sales &amp; support parameters
              </summary>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                  marginTop: 10,
                  padding: 10,
                  background: "var(--cb-98)",
                  borderRadius: 6,
                }}
              >
                <NumberPair
                  label="Deal size range ($)"
                  values={p.sales?.deal_size_range ?? [10000, 100000]}
                  onChange={(v) =>
                    updateProduct(i, { sales: { ...p.sales, deal_size_range: v } })
                  }
                  readOnly={readOnly}
                />
                <NumberField
                  label="Win rate modifier"
                  value={p.sales?.win_rate_modifier ?? 1.0}
                  step={0.1}
                  onChange={(v) =>
                    updateProduct(i, { sales: { ...p.sales, win_rate_modifier: v } })
                  }
                  readOnly={readOnly}
                />
                <NumberPair
                  label="Sales cycle (days)"
                  values={p.sales?.cycle_days_range ?? [30, 90]}
                  onChange={(v) =>
                    updateProduct(i, { sales: { ...p.sales, cycle_days_range: v } })
                  }
                  readOnly={readOnly}
                />
                <NumberField
                  label="Selection weight"
                  value={p.sales?.selection_weight ?? 0.1}
                  step={0.05}
                  onChange={(v) =>
                    updateProduct(i, { sales: { ...p.sales, selection_weight: v } })
                  }
                  readOnly={readOnly}
                />
                <NumberField
                  label="Cases / 100 installs / qtr"
                  value={p.support?.cases_per_100_installs_quarter ?? 5}
                  step={1}
                  onChange={(v) =>
                    updateProduct(i, {
                      support: { ...p.support, cases_per_100_installs_quarter: v },
                    })
                  }
                  readOnly={readOnly}
                />
                <NumberField
                  label="CSAT modifier"
                  value={p.support?.csat_modifier ?? 0}
                  step={0.1}
                  onChange={(v) =>
                    updateProduct(i, { support: { ...p.support, csat_modifier: v } })
                  }
                  readOnly={readOnly}
                />
              </div>
            </details>
          </div>
          );
        })}
        {products.length === 0 && !readOnly && (
          <div
            className="card"
            style={{
              padding: 24,
              textAlign: "center",
              color: "var(--ink-3)",
              fontSize: 12,
              borderStyle: "dashed",
            }}
          >
            No products yet. Add one above, or use AI refine to draft a product line.
          </div>
        )}
      </div>
    </div>
  );
}

function ProductInput({
  value,
  placeholder,
  onChange,
  readOnly,
  style,
}: {
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      style={{
        width: "100%",
        padding: "6px 8px",
        fontSize: 12,
        border: "1px solid var(--bd-2)",
        borderRadius: 4,
        outline: "none",
        background: readOnly ? "var(--cb-98)" : "white",
        color: "var(--ink)",
        ...style,
      }}
    />
  );
}

function NumberField({
  label,
  value,
  step,
  onChange,
  readOnly,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
  readOnly: boolean;
}): JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".06em" }}>
        {label}
      </span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        readOnly={readOnly}
        style={{
          padding: "5px 8px",
          fontSize: 12,
          border: "1px solid var(--bd-2)",
          borderRadius: 4,
          outline: "none",
          background: readOnly ? "var(--cb-98)" : "white",
        }}
      />
    </label>
  );
}

function NumberPair({
  label,
  values,
  onChange,
  readOnly,
}: {
  label: string;
  values: [number, number];
  onChange: (v: [number, number]) => void;
  readOnly: boolean;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".06em" }}>
        {label}
      </span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <input
          type="number"
          value={values[0]}
          onChange={(e) => onChange([Number(e.target.value), values[1]])}
          readOnly={readOnly}
          style={{
            flex: 1,
            padding: "5px 8px",
            fontSize: 12,
            border: "1px solid var(--bd-2)",
            borderRadius: 4,
            outline: "none",
            background: readOnly ? "var(--cb-98)" : "white",
          }}
        />
        <span style={{ color: "var(--ink-3)" }}>–</span>
        <input
          type="number"
          value={values[1]}
          onChange={(e) => onChange([values[0], Number(e.target.value)])}
          readOnly={readOnly}
          style={{
            flex: 1,
            padding: "5px 8px",
            fontSize: 12,
            border: "1px solid var(--bd-2)",
            borderRadius: 4,
            outline: "none",
            background: readOnly ? "var(--cb-98)" : "white",
          }}
        />
      </div>
    </div>
  );
}

function VersionHistoryPanel({
  versions,
  onRestore,
}: {
  versions: PackVersion[];
  onRestore: (versionNumber: number) => void;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: 14, position: "sticky", top: 80 }}>
      <div className="label" style={{ marginBottom: 8 }}>
        Version history
      </div>
      <div style={{ display: "grid", gap: 4, maxHeight: 400, overflowY: "auto" }}>
        {versions.map((v) => (
          <button
            key={v.id}
            onClick={() => onRestore(v.version_number)}
            title="Click to restore this version into the editor"
            style={{
              textAlign: "left",
              padding: "6px 8px",
              border: "1px solid var(--bd-2)",
              borderRadius: 6,
              background: "white",
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 12, color: "var(--ink)" }}>
                v{v.version_number}
              </span>
              <span style={{ fontSize: 10, color: "var(--ink-3)" }}>
                {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
              </span>
            </div>
            {v.change_summary && (
              <div
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  marginTop: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {v.change_summary}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function JsonTab({
  jsonText,
  setJsonText,
  jsonError,
  onApply,
  readOnly,
}: {
  jsonText: string;
  setJsonText: (s: string) => void;
  jsonError: string | null;
  onApply: () => void;
  readOnly: boolean;
}): JSX.Element {
  return (
    <div>
      <textarea
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        readOnly={readOnly}
        spellCheck={false}
        style={{
          width: "100%",
          minHeight: 480,
          padding: 14,
          fontFamily: "ui-monospace, Menlo, monospace",
          fontSize: 11,
          lineHeight: 1.6,
          border: "1px solid var(--bd)",
          borderRadius: 10,
          background: "#0f1e36",
          color: "#d8e3f3",
          outline: "none",
          resize: "vertical",
        }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, alignItems: "center" }}>
        <div style={{ fontSize: 12, color: jsonError ? "#7a1d1d" : "var(--ink-3)" }}>
          {jsonError ? `JSON error — ${jsonError}` : "Edit raw pack JSON · click Apply to load it back into the editor."}
        </div>
        {!readOnly && (
          <button onClick={onApply} className="btn ghost">
            Apply JSON
          </button>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  readOnly,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly: boolean;
  multiline?: boolean;
}): JSX.Element {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="label" style={{ marginBottom: 6 }}>{label}</div>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          rows={4}
          style={{
            width: "100%",
            padding: 10,
            fontSize: 13,
            border: "1px solid var(--bd)",
            borderRadius: 8,
            outline: "none",
            background: "white",
            resize: "vertical",
          }}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={{
            width: "100%",
            padding: 10,
            fontSize: 13,
            border: "1px solid var(--bd)",
            borderRadius: 8,
            outline: "none",
            background: "white",
          }}
        />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────
// PR 3 Week 2 — new tabs
// ──────────────────────────────────────────────────────────────────


// Tableau Next (Phase 8 / direct authoring) editor.
//
// Reads + writes draft.tableau_next per the PR 2 schema:
//   { workspace_name, default_role_visibility, boards[], metrics[],
//     permissions[], sdm_api_name }
//
// Schema-driven: board / metric source_canonical dropdowns enumerate
// from app/services/tableau_next/templates so adding a new template
// never requires a code change here.
interface TableauNextBlock {
  workspace_name?: string;
  default_role_visibility?: string[];
  boards?: { name?: string; source_canonical: string; overrides?: Record<string, unknown> }[];
  metrics?: { name?: string; source_canonical: string; overrides?: Record<string, unknown> }[];
  permissions?: { principal_type?: string; principal_id: string; access_level?: string }[];
  sdm_api_name?: string;
}

function TableauNextTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: PackJson;
  onChange: (next: TableauNextBlock | undefined) => void;
  readOnly: boolean;
}): JSX.Element {
  const block = (draft.tableau_next ?? {}) as TableauNextBlock;
  const isEmpty =
    !block.workspace_name
    && !(block.boards?.length ?? 0)
    && !(block.metrics?.length ?? 0);

  const update = (mut: Partial<TableauNextBlock>): void => {
    onChange({ ...block, ...mut });
  };

  const useDefaults = (): void => {
    const companyName =
      (draft.company as { name?: string } | undefined)?.name
      ?? "Untitled Pack";
    onChange({
      workspace_name: `${companyName} Demo`,
      boards: [
        { name: "Pipeline Health", source_canonical: "sales_pipeline" },
        { name: "Service SLA", source_canonical: "service_sla" },
        { name: "Activity Coverage", source_canonical: "activity_coverage" },
        { name: "Account 360", source_canonical: "account_360" },
      ],
      metrics: [
        { name: "Pipeline coverage", source_canonical: "pipeline_coverage" },
        { name: "Win rate (12w)", source_canonical: "win_rate_trend" },
        { name: "Avg time-to-handoff", source_canonical: "avg_handoff_sla" },
      ],
    });
  };

  const boardOptions = boardCanonicalOptions();
  const metricOptions = metricCanonicalOptions();

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Drives Phase 8 — direct Tab Next workspace authoring.
        Replaces ATF Apps for non-Sales-Insights packs. The wizard
        hides this for SI / Service-Insights packs.
      </div>

      {isEmpty && !readOnly && (
        <button
          type="button"
          onClick={useDefaults}
          style={{
            padding: "10px 14px",
            border: "1px solid var(--violet)",
            borderRadius: 8,
            background: "rgba(168,110,233,.08)",
            color: "var(--violet)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            alignSelf: "flex-start",
          }}
        >
          Use defaults (4 boards + 3 metrics)
        </button>
      )}

      <LabeledInput label="Workspace label">
        <input
          value={block.workspace_name ?? ""}
          onChange={(e) => update({ workspace_name: e.target.value })}
          readOnly={readOnly}
          placeholder={`e.g. ${(draft.company as { name?: string } | undefined)?.name ?? "Pack"} Demo`}
          style={inputStyle}
        />
      </LabeledInput>

      <BoardsEditor
        boards={block.boards ?? []}
        options={boardOptions}
        onChange={(next) => update({ boards: next })}
        readOnly={readOnly}
      />

      <MetricsEditor
        metrics={block.metrics ?? []}
        options={metricOptions}
        onChange={(next) => update({ metrics: next })}
        readOnly={readOnly}
      />

      <LabeledInput label="SDM API name (override)">
        <input
          value={block.sdm_api_name ?? ""}
          onChange={(e) => update({ sdm_api_name: e.target.value || undefined })}
          readOnly={readOnly}
          placeholder="Auto — uses Phase 6c output if blank"
          style={inputStyle}
        />
      </LabeledInput>
    </div>
  );
}


function LabeledInput({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div style={subSectionLabelStyle}>{label}</div>
      {children}
    </div>
  );
}


function BoardsEditor({
  boards,
  options,
  onChange,
  readOnly,
}: {
  boards: NonNullable<TableauNextBlock["boards"]>;
  options: string[];
  onChange: (next: NonNullable<TableauNextBlock["boards"]>) => void;
  readOnly: boolean;
}): JSX.Element {
  const addBoard = (): void => {
    onChange([
      ...boards,
      { name: "", source_canonical: options[0] ?? "sales_pipeline" },
    ]);
  };
  const updateBoard = (i: number, mut: Partial<NonNullable<TableauNextBlock["boards"]>[number]>): void => {
    onChange(boards.map((b, idx) => (idx === i ? { ...b, ...mut } : b)));
  };
  const removeBoard = (i: number): void => {
    onChange(boards.filter((_, idx) => idx !== i));
  };
  return (
    <div>
      <div style={subSectionLabelStyle}>Boards (Phase 8 dashboards)</div>
      <div style={{ display: "grid", gap: 8 }}>
        {boards.map((b, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            <input
              value={b.name ?? ""}
              onChange={(e) => updateBoard(i, { name: e.target.value })}
              readOnly={readOnly}
              placeholder="Display name"
              style={inputStyle}
            />
            <select
              value={b.source_canonical}
              onChange={(e) => updateBoard(i, { source_canonical: e.target.value })}
              disabled={readOnly}
              style={inputStyle}
            >
              {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeBoard(i)}
              disabled={readOnly}
              style={removeButtonStyle}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      {!readOnly && (
        <button type="button" onClick={addBoard} style={addButtonStyle}>
          + Add board
        </button>
      )}
    </div>
  );
}


function MetricsEditor({
  metrics,
  options,
  onChange,
  readOnly,
}: {
  metrics: NonNullable<TableauNextBlock["metrics"]>;
  options: string[];
  onChange: (next: NonNullable<TableauNextBlock["metrics"]>) => void;
  readOnly: boolean;
}): JSX.Element {
  const add = (): void => {
    onChange([
      ...metrics,
      { name: "", source_canonical: options[0] ?? "pipeline_coverage" },
    ]);
  };
  const update = (i: number, mut: Partial<NonNullable<TableauNextBlock["metrics"]>[number]>): void => {
    onChange(metrics.map((m, idx) => (idx === i ? { ...m, ...mut } : m)));
  };
  const remove = (i: number): void => {
    onChange(metrics.filter((_, idx) => idx !== i));
  };
  return (
    <div>
      <div style={subSectionLabelStyle}>Tab Next SDM Metrics (in-org, NOT Cloud Pulse)</div>
      <div style={{ display: "grid", gap: 8 }}>
        {metrics.map((m, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr auto",
              gap: 8,
              alignItems: "center",
            }}
          >
            <input
              value={m.name ?? ""}
              onChange={(e) => update(i, { name: e.target.value })}
              readOnly={readOnly}
              placeholder="Display name"
              style={inputStyle}
            />
            <select
              value={m.source_canonical}
              onChange={(e) => update(i, { source_canonical: e.target.value })}
              disabled={readOnly}
              style={inputStyle}
            >
              {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={readOnly}
              style={removeButtonStyle}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      {!readOnly && (
        <button type="button" onClick={add} style={addButtonStyle}>
          + Add metric
        </button>
      )}
    </div>
  );
}


// Cloud Pulse tab — drives Phase 7 PR 1 path. NOT the same as
// Tab Next SDM Metrics (PR 2 / Phase 8).
interface CloudPulseBlock {
  build_tdsx?: boolean;
  build_twbx?: boolean;
  publish_to_cloud?: boolean;
  create_pulse_metrics?: boolean;
  pulse_metrics?: {
    name: string;
    classification: string;
    field?: string;
    subscription_channel?: string;
    alerts?: Record<string, unknown>;
  }[];
}

function CloudPulseTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: PackJson;
  onChange: (next: CloudPulseBlock | undefined) => void;
  readOnly: boolean;
}): JSX.Element {
  const block = (draft.tableau_outputs ?? {}) as CloudPulseBlock;
  const update = (mut: Partial<CloudPulseBlock>): void => onChange({ ...block, ...mut });

  const updateMetric = (
    i: number,
    mut: Partial<NonNullable<CloudPulseBlock["pulse_metrics"]>[number]>,
  ): void => {
    const next = (block.pulse_metrics ?? []).map((m, idx) =>
      idx === i ? { ...m, ...mut } : m,
    );
    update({ pulse_metrics: next });
  };
  const removeMetric = (i: number): void => {
    update({
      pulse_metrics: (block.pulse_metrics ?? []).filter((_, idx) => idx !== i),
    });
  };
  const addMetric = (): void => {
    const opts = pulseClassificationOptions();
    update({
      pulse_metrics: [
        ...(block.pulse_metrics ?? []),
        { name: "", classification: opts[0] ?? "Flow" },
      ],
    });
  };

  const classOptions = pulseClassificationOptions();
  const channelOptions = pulseSubscriptionChannelOptions();

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Cloud Pulse drives Phase 7 (Tableau Cloud publish + Pulse
        metric authoring). Distinct from the Tab Next SDM Metrics
        tab — different APIs, different consumers. See PR 1 / PR 2
        terminology pin in the design docs.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8 }}>
        <Toggle
          label="Build .tdsx"
          on={block.build_tdsx ?? true}
          onChange={(v) => update({ build_tdsx: v })}
          readOnly={readOnly}
        />
        <Toggle
          label="Build .twbx"
          on={!!block.build_twbx}
          onChange={(v) => update({ build_twbx: v })}
          readOnly={readOnly}
        />
        <Toggle
          label="Publish to Cloud"
          on={!!block.publish_to_cloud}
          onChange={(v) => update({ publish_to_cloud: v })}
          readOnly={readOnly}
        />
        <Toggle
          label="Create Pulse metrics"
          on={!!block.create_pulse_metrics}
          onChange={(v) => update({ create_pulse_metrics: v })}
          readOnly={readOnly}
        />
      </div>

      <div>
        <div style={subSectionLabelStyle}>Pulse metrics</div>
        <div style={{ display: "grid", gap: 8 }}>
          {(block.pulse_metrics ?? []).map((m, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                value={m.name}
                onChange={(e) => updateMetric(i, { name: e.target.value })}
                readOnly={readOnly}
                placeholder="Metric name"
                style={inputStyle}
              />
              <select
                value={m.classification}
                onChange={(e) => updateMetric(i, { classification: e.target.value })}
                disabled={readOnly}
                style={inputStyle}
              >
                {classOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <select
                value={m.subscription_channel ?? "owner"}
                onChange={(e) => updateMetric(i, { subscription_channel: e.target.value })}
                disabled={readOnly}
                style={inputStyle}
              >
                {channelOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeMetric(i)}
                disabled={readOnly}
                style={removeButtonStyle}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {!readOnly && (
          <button type="button" onClick={addMetric} style={addButtonStyle}>
            + Add Pulse metric
          </button>
        )}
      </div>
    </div>
  );
}


// Date Shift tab (PR 4) — drives the per-pack Apex date-shift bundle
// that runs against the org on a daily cadence. When the block is
// empty, the legacy global GDO_DateShiftScheduled.cls handles the
// org with the canonical default field list.
interface DateShiftBlock {
  targets?: { sobject: string; fields: string[] }[];
  schedule?: "daily-2am" | "weekly-sunday" | "off";
  anchor_offset_days?: number;
}

function DateShiftTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: PackJson;
  onChange: (next: DateShiftBlock | undefined) => void;
  readOnly: boolean;
}): JSX.Element {
  const block = (draft.date_shift ?? {}) as DateShiftBlock;
  const isEmpty =
    !(block.targets?.length ?? 0)
    && !block.schedule
    && (block.anchor_offset_days ?? 0) === 0;

  const update = (mut: Partial<DateShiftBlock>): void => {
    onChange({ ...block, ...mut });
  };

  const useDefaults = (): void => {
    onChange({
      targets: [
        { sobject: "Opportunity",            fields: ["CloseDate", "DateTimeCreated__c"] },
        { sobject: "Case",                   fields: ["Closed_Date__c", "Created_Date__c"] },
        { sobject: "Lead",                   fields: ["Created_Date__c"] },
        { sobject: "OpportunityHistory2__c", fields: ["CreatedDate__c", "CloseDate__c"] },
        { sobject: "CaseHistoryC__c",        fields: ["CreatedDate__c"] },
      ],
      schedule: "daily-2am",
      anchor_offset_days: 0,
    });
  };

  const targets = block.targets ?? [];

  const updateTarget = (i: number, mut: Partial<{ sobject: string; fields: string[] }>): void => {
    update({
      targets: targets.map((t, idx) => (idx === i ? { ...t, ...mut } : t)),
    });
  };
  const removeTarget = (i: number): void => {
    update({ targets: targets.filter((_, idx) => idx !== i) });
  };
  const addTarget = (): void => {
    update({
      targets: [...targets, { sobject: "", fields: [] }],
    });
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Per-pack Apex bundle that shifts demo data dates forward
        on a cadence. Generated as <code>{
          ((draft.company as { external_id_prefix?: string } | undefined)?.external_id_prefix
            ?? "&lt;PREFIX&gt;")
        }_DateShiftConfig</code> + <code>{
          ((draft.company as { external_id_prefix?: string } | undefined)?.external_id_prefix
            ?? "&lt;PREFIX&gt;")
        }_DateShiftScheduled</code>. Packs with no block fall through
        to the legacy global Schedulable.
      </div>

      {isEmpty && !readOnly && (
        <button
          type="button"
          onClick={useDefaults}
          style={{
            padding: "10px 14px",
            border: "1px solid var(--violet)",
            borderRadius: 8,
            background: "rgba(168,110,233,.08)",
            color: "var(--violet)",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            alignSelf: "flex-start",
          }}
        >
          Use defaults (canonical 5-target list, daily 2am)
        </button>
      )}

      <LabeledInput label="Schedule">
        <select
          value={block.schedule ?? "daily-2am"}
          onChange={(e) => update({ schedule: e.target.value as DateShiftBlock["schedule"] })}
          disabled={readOnly}
          style={inputStyle}
        >
          <option value="daily-2am">daily-2am (every day, 2am org-local)</option>
          <option value="weekly-sunday">weekly-sunday (Sunday 2am)</option>
          <option value="off">off (deploy classes, skip cron)</option>
        </select>
      </LabeledInput>

      <LabeledInput label="Anchor offset (days)">
        <input
          type="number"
          value={block.anchor_offset_days ?? 0}
          onChange={(e) => update({ anchor_offset_days: parseInt(e.target.value, 10) || 0 })}
          readOnly={readOnly}
          min={-365}
          max={365}
          style={inputStyle}
        />
      </LabeledInput>

      <div>
        <div style={subSectionLabelStyle}>Targets</div>
        <div style={{ display: "grid", gap: 8 }}>
          {targets.map((t, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr auto",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                value={t.sobject}
                onChange={(e) => updateTarget(i, { sobject: e.target.value })}
                readOnly={readOnly}
                placeholder="sObject (e.g. Opportunity)"
                style={inputStyle}
              />
              <input
                value={t.fields.join(", ")}
                onChange={(e) =>
                  updateTarget(i, {
                    fields: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                readOnly={readOnly}
                placeholder="Comma-separated field API names (CloseDate, CreatedDate__c)"
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => removeTarget(i)}
                disabled={readOnly}
                style={removeButtonStyle}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {!readOnly && (
          <button type="button" onClick={addTarget} style={addButtonStyle}>
            + Add target
          </button>
        )}
        {targets.length > 0 && (
          <div
            style={{
              marginTop: 10,
              fontSize: 11,
              color: "var(--ink-3)",
              fontStyle: "italic",
            }}
          >
            Field names are pre-flighted via Schema describe before
            deploy — missing fields are pruned with a warning so the
            Apex always compiles.
          </div>
        )}
      </div>
    </div>
  );
}


// Tab Extensions tab (PR 6) — chat surface, not a form. SE
// describes what they want in NL; AI proposes a tab_extensions[]
// entry; iframe renders the live preview; "Commit to pack" writes
// the entry into draft.tab_extensions.bundles.
interface TabExtensionBundle {
  slug: string;
  template: string;
  title_override?: string;
  config?: Record<string, unknown>;
  ai_brief?: string;
  ai_iterations?: number;
}

interface TabExtensionsBlock {
  bundles?: TabExtensionBundle[];
}

interface ChatHistoryEntry {
  role: "user" | "assistant";
  content: string;
}

function TabExtensionsTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: PackJson;
  onChange: (next: TabExtensionsBlock) => void;
  readOnly: boolean;
}): JSX.Element {
  const block = (draft.tab_extensions ?? {}) as TabExtensionsBlock;
  const existing: TabExtensionBundle[] = block.bundles ?? [];

  const [history, setHistory] = useState<ChatHistoryEntry[]>([]);
  const [draftMessage, setDraftMessage] = useState("");
  const [proposed, setProposed] = useState<TabExtensionBundle | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [rationale, setRationale] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [pending, setPending] = useState(false);

  const sendMessage = async (): Promise<void> => {
    const message = draftMessage.trim();
    if (!message || readOnly || pending) return;
    setPending(true);
    setErrors([]);
    const nextHistory: ChatHistoryEntry[] = [
      ...history,
      { role: "user", content: message },
    ];
    setHistory(nextHistory);
    setDraftMessage("");
    try {
      const r = await (api as { tabExtensionsChat?: (p: unknown) => Promise<{
        proposed_bundle: TabExtensionBundle | null;
        preview_html: string | null;
        rationale: string | null;
        iterations: number;
        errors: string[];
      } | null> }).tabExtensionsChat?.({
        pack: draft as unknown as Record<string, unknown>,
        user_message: message,
        history: history,
        current_proposal: proposed as unknown as Record<string, unknown> | null,
      });
      if (!r) {
        setErrors(["Chat backend unreachable. Check your connection."]);
        return;
      }
      setProposed(r.proposed_bundle ?? null);
      setPreviewHtml(r.preview_html);
      setRationale(r.rationale);
      setErrors(r.errors || []);
      if (r.rationale) {
        setHistory([...nextHistory, { role: "assistant", content: r.rationale }]);
      }
    } catch (e) {
      setErrors([(e as Error).message || "Chat call failed"]);
    } finally {
      setPending(false);
    }
  };

  const commit = (): void => {
    if (!proposed) return;
    onChange({
      ...block,
      bundles: [
        ...existing.filter((b) => b.slug !== proposed.slug),
        proposed,
      ],
    });
    setProposed(null);
    setPreviewHtml(null);
    setRationale(null);
    setHistory([]);
    setErrors([]);
  };

  const discard = (): void => {
    setProposed(null);
    setPreviewHtml(null);
    setRationale(null);
  };

  const removeExisting = (slug: string): void => {
    onChange({
      ...block,
      bundles: existing.filter((b) => b.slug !== slug),
    });
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Describe a Tableau Dashboard Extension you want for this
        pack — a button, a status panel, anything that helps your
        demo. The Generator drops the .trex into the .twbx and
        hosts the bundle on Heroku.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
          gap: 16,
          minHeight: 320,
        }}
      >
        {/* Chat column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid var(--bd)",
            borderRadius: 10,
            background: "var(--bg-2)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: 1,
              padding: 12,
              overflow: "auto",
              fontSize: 13,
              minHeight: 220,
            }}
          >
            {history.length === 0 && !pending && (
              <div style={{ color: "var(--ink-3)" }}>
                Tell the Generator what you want this extension to do.
                Try: <em>"I want a button that resets dates and shows
                the live opp count."</em>
              </div>
            )}
            {history.map((m, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 10,
                  padding: 8,
                  borderRadius: 6,
                  background:
                    m.role === "user" ? "white" : "rgba(11,92,171,.06)",
                  border: "1px solid var(--bd-2)",
                  color: "var(--ink)",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--ink-3)",
                    marginBottom: 4,
                  }}
                >
                  {m.role === "user" ? "YOU" : "GENERATOR"}
                </div>
                {m.content}
              </div>
            ))}
            {pending && (
              <div style={{ color: "var(--ink-3)", fontStyle: "italic" }}>
                Generator is proposing…
              </div>
            )}
            {errors.map((e, i) => (
              <div
                key={i}
                style={{
                  padding: 6,
                  background: "rgba(239,83,80,.06)",
                  border: "1px solid rgba(239,83,80,.4)",
                  borderRadius: 6,
                  color: "#b71c1c",
                  fontSize: 12,
                  marginBottom: 6,
                }}
              >
                {e}
              </div>
            ))}
          </div>
          <div
            style={{
              borderTop: "1px solid var(--bd)",
              padding: 10,
              display: "flex",
              gap: 6,
              background: "white",
            }}
          >
            <input
              type="text"
              value={draftMessage}
              onChange={(e) => setDraftMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) sendMessage();
              }}
              placeholder="Describe the extension you want…"
              disabled={readOnly || pending}
              style={{
                flex: 1,
                padding: 8,
                fontSize: 13,
                border: "1px solid var(--bd)",
                borderRadius: 6,
                outline: "none",
                background: "white",
                color: "var(--ink)",
              }}
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={readOnly || pending || !draftMessage.trim()}
              className="btn primary sm"
            >
              Send
            </button>
          </div>
        </div>

        {/* Preview column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid var(--bd)",
            borderRadius: 10,
            background: "white",
            overflow: "hidden",
          }}
        >
          {previewHtml ? (
            <iframe
              title="Tab Extension preview"
              sandbox="allow-scripts"
              srcDoc={previewHtml}
              style={{ flex: 1, border: 0, minHeight: 220, background: "white" }}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--ink-3)",
                fontSize: 12,
                padding: 20,
                textAlign: "center",
              }}
            >
              Live preview lands here once the Generator proposes a
              bundle. Iterate by replying in the chat.
            </div>
          )}
          {proposed && (
            <div
              style={{
                borderTop: "1px solid var(--bd)",
                padding: 10,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "var(--bg-2)",
              }}
            >
              <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                <strong>{proposed.slug}</strong> · template:{" "}
                <code>{proposed.template}</code>
                {rationale && (
                  <div style={{ marginTop: 4, fontSize: 10, fontStyle: "italic" }}>
                    {rationale}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={discard}
                  className="btn ghost sm"
                  disabled={readOnly}
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={commit}
                  className="btn primary sm"
                  disabled={readOnly}
                >
                  Commit to pack
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {existing.length > 0 && (
        <div>
          <div style={subSectionLabelStyle}>
            Existing bundles ({existing.length})
          </div>
          <div style={{ display: "grid", gap: 6 }}>
            {existing.map((b) => (
              <div
                key={b.slug}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  gap: 8,
                  padding: 8,
                  border: "1px solid var(--bd)",
                  borderRadius: 6,
                  alignItems: "center",
                  fontSize: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                    {b.title_override || b.slug}
                  </div>
                  <div style={{ color: "var(--ink-3)", fontSize: 11 }}>
                    {b.slug}
                  </div>
                </div>
                <div style={{ color: "var(--ink-2)" }}>
                  Template: <code>{b.template}</code>
                </div>
                <button
                  type="button"
                  onClick={() => removeExisting(b.slug)}
                  className="btn ghost sm"
                  style={{ color: "var(--ink-3)" }}
                  disabled={readOnly}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Stories tab — lifts stories.descriptions out of the JSON tab into
// a per-story textarea form. The schema requires all 16 IDs; missing
// any flags an error in the Validate tab.
function StoriesTab({
  draft,
  onChange,
  readOnly,
}: {
  draft: PackJson;
  onChange: (next: { descriptions: Record<string, string>; [k: string]: unknown }) => void;
  readOnly: boolean;
}): JSX.Element {
  const stories = (draft.stories ?? {}) as Record<string, unknown>;
  const descriptions = (stories.descriptions ?? {}) as Record<string, string>;

  const updateDescription = (id: string, text: string): void => {
    onChange({
      ...stories,
      descriptions: { ...descriptions, [id]: text },
    });
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        The 16 canonical story IDs the runtime expects. Missing any
        flag an error in the Validate tab. AI-generated packs auto-
        populate them; hand-authored packs must fill all 16.
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {STORY_IDS.map((id) => (
          <div key={id}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ink-3)",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              {id}
            </div>
            <textarea
              value={descriptions[id] ?? ""}
              onChange={(e) => updateDescription(id, e.target.value)}
              readOnly={readOnly}
              rows={2}
              placeholder={`Describe the ${id.replace(/_/g, " ")} narrative…`}
              style={{
                width: "100%",
                padding: 10,
                fontSize: 13,
                fontFamily: "inherit",
                border: descriptions[id] ? "1px solid var(--bd)" : "1px solid #f0a000",
                borderRadius: 8,
                outline: "none",
                resize: "vertical",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}


// Validate tab — runs ajv against the schema in the browser.
//
// Output shape mirrors the backend's ``validate_pack_structured``
// {path, message, severity} contract so what the SE sees here is
// what the route returns on save.
function ValidateTab({ draft }: { draft: PackJson }): JSX.Element {
  const issues = useMemo(() => {
    const ajv = new Ajv2020({ allErrors: true, strict: false });
    const validate = ajv.compile(packSchema);
    const ok = validate(draft);
    if (ok) return [] as { path: string; message: string }[];
    return (validate.errors ?? []).map((err) => ({
      path: (err.instancePath || err.schemaPath || "<root>").replace(/^\//, "").replace(/\//g, "."),
      message: err.message ?? "Invalid",
    }));
  }, [draft]);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
        Live validation against schema/pack_v1.schema.json. The
        backend runs the same schema on save — what you see here is
        exactly what the route returns.
      </div>
      {issues.length === 0 ? (
        <div
          style={{
            padding: "12px 14px",
            background: "rgba(76,175,80,.08)",
            border: "1px solid rgba(76,175,80,.4)",
            borderRadius: 8,
            color: "#1b5e20",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          ✓ Pack is valid — every section satisfies the schema.
        </div>
      ) : (
        <div
          style={{
            padding: "12px 14px",
            background: "rgba(239,83,80,.06)",
            border: "1px solid rgba(239,83,80,.35)",
            borderRadius: 8,
          }}
        >
          <div style={{ fontWeight: 600, color: "#b71c1c", marginBottom: 8 }}>
            ✗ {issues.length} issue{issues.length === 1 ? "" : "s"}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--ink-2)" }}>
            {issues.slice(0, 50).map((iss, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                <code style={{ color: "var(--ink)" }}>{iss.path || "<root>"}</code>: {iss.message}
              </li>
            ))}
            {issues.length > 50 && (
              <li style={{ color: "var(--ink-3)" }}>…and {issues.length - 50} more</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}


// ── Shared style tokens for the new tabs ──

function Toggle({
  label,
  on,
  onChange,
  readOnly,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
  readOnly: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => !readOnly && onChange(!on)}
      disabled={readOnly}
      style={{
        padding: 10,
        border: `1px solid ${on ? "var(--violet)" : "var(--bd)"}`,
        borderRadius: 8,
        background: on ? "rgba(168,110,233,.08)" : "white",
        color: on ? "var(--violet)" : "var(--ink-2)",
        fontSize: 13,
        fontWeight: 600,
        cursor: readOnly ? "default" : "pointer",
        textAlign: "left",
      }}
    >
      {on ? "✓ " : "○ "}
      {label}
    </button>
  );
}


const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: 10,
  fontSize: 13,
  border: "1px solid var(--bd)",
  borderRadius: 8,
  outline: "none",
  background: "white",
};

const subSectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--ink-3)",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const addButtonStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 14px",
  fontSize: 12,
  fontWeight: 600,
  border: "1px dashed var(--bd)",
  borderRadius: 8,
  background: "transparent",
  color: "var(--ink-2)",
  cursor: "pointer",
};

const removeButtonStyle: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 600,
  border: "1px solid var(--bd)",
  borderRadius: 6,
  background: "transparent",
  color: "var(--ink-3)",
  cursor: "pointer",
};

function EditorMessage({ title, message }: { title: string; message: string }): JSX.Element {
  return (
    <div style={{ maxWidth: 720, margin: "60px auto", padding: 28, textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)", marginBottom: 6 }}>
        {title}
      </div>
      {message && <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{message}</div>}
    </div>
  );
}

// Re-export the soft-fetch helper so we don't trip the unused import lint.
void apiSoft;
